import clamp from 'clamp';

// Color set used for the avatars that don't have a photo and use a colored bg/text with name initials.
export const anonymousColors = [
    { background: '#89C6DA', text: '#275564' },
    { background: '#FFBE82', text: '#8E521A' },
    { background: '#91eabf', text: '#0b4f2f' },
    { background: '#b2a5ff', text: '#442fc8' },
    { background: '#EF96B8', text: '#A12958' },
    { background: '#B3E5FC', text: '#2B6B7E' },
    { background: '#daffb3', text: '#4e6e1b' },
    { background: '#e98686', text: '#87003b' },
    { background: '#FFDCBC', text: '#ff912c' },
    { background: '#ddb5ff', text: '#8855ff' },
    { background: '#B8E986', text: '#2F5A1A' },
    { background: '#DCEEF4', text: '#3E6C7B' },
    { background: '#ff918f', text: '#9a1616' },
    { background: '#7FDBFF', text: '#0F5D6B' },
    { background: '#e9dd86', text: '#818700' },
    { background: '#4dde98', text: '#0b4f2f' },
    { background: '#ffb3e3', text: '#ff55c0' },
];

// Color set for the real-time color indicating a user is currently active in the document.
export const realtimeColors = [
    '#b2a5ff',
    '#ffcccb',
    '#FFBE82',
    '#4dde98',
    '#ffecc2',
    '#B3E5FC',
    '#B8E986',
    '#ff918f',
    '#7FDBFF',
    '#daffb3',
    '#dad4ff',
    '#FFD882',
    '#91eabf',
    '#89C6DA',
    '#FFDCBC',
    '#EF96B8',
    '#DCEEF4',
];

/**
 * Returns colors to construct an avatar with initials as fallback if user has no photo url.
 */
export const getAnonymousColors = createUniqueGetter(anonymousColors);

/**
 * Return a color for a participant.
 */
export const getRealTimeColor = createUniqueGetter(realtimeColors, (color) => ({
    base: color,
    faded: addAlpha(color, 0.2),
}));

/**
 * Create a unique getter function that returns a color, from a passed color set, based on an id. It remembers which colors have been assigned to which ids.
 */
function createUniqueGetter<T, U = T>(
    colors: T[],
    transform?: (color: T) => U
): (id: string | undefined | null) => U {
    const cache: Record<string, U> = {};

    return function getColor(id: string | undefined | null): U {
        // Default to the first color if no ID is provided.
        if (!id) {
            const [color] = colors;
            return transform ? transform(color) : (color as unknown as U);
        }

        // If the color is known to have already been assigned, use that
        if (cache[id]) {
            return cache[id];
        }

        const colorIndex = hashStringToIndex(id, colors);
        const color = colors[colorIndex];
        const transformedColor = transform ? transform(color) : (color as unknown as U);
        cache[id] = transformedColor;

        return transformedColor;
    };
}

// This function provides a deterministic way to hash a string to an index in an array.
function hashStringToIndex<T>(string: string, array: T[]): number {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        const character = string.charCodeAt(i);
        hash = (hash << 5) - hash + character;
    }
    return Math.abs(hash) % array.length;
}

/**
 * Update a hex color adding the alpha channel.
 */
function addAlpha(color: string, opacity: number): string {
    const hexOpacity = Math.round(clamp(opacity || 1, 0, 1) * 255);
    return color + hexOpacity.toString(16).toUpperCase();
}
